export type FileItem = {
  id: string;
  media_type: "image" | "video";
  mime_type: string;
  original_name: string;
  byte_size: number;
  width: number | null;
  height: number | null;
  duration_ms: number | null;
  thumbnail_ready: boolean;
  uploaded_at: string;
  is_mine: boolean;
};

export type ListResponse = { items: FileItem[] };
export type AuthCheck = { ok: true; is_admin: boolean; label: string | null };
export type Health = { ok: boolean; files: number; disk_free_gb: number };

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

let _token: string | null = null;
export function setAuthToken(token: string | null) {
  _token = token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (_token) headers.set("Authorization", `Bearer ${_token}`);
  const r = await fetch(path, { ...init, headers, credentials: "include" });
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new ApiError(r.status, text || r.statusText);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  authCheck: () => request<AuthCheck>("/api/auth/check"),
  health: () => request<Health>("/api/health"),
  listFiles: (params: { order?: "asc" | "desc"; limit?: number; afterId?: string }) => {
    const u = new URLSearchParams();
    if (params.order) u.set("order", params.order);
    if (params.limit) u.set("limit", String(params.limit));
    if (params.afterId) u.set("after_id", params.afterId);
    return request<ListResponse>(`/api/files?${u.toString()}`);
  },
  listMyFiles: (params: { order?: "asc" | "desc"; limit?: number; afterId?: string }) => {
    const u = new URLSearchParams();
    if (params.order) u.set("order", params.order);
    if (params.limit) u.set("limit", String(params.limit));
    if (params.afterId) u.set("after_id", params.afterId);
    return request<ListResponse>(`/api/files/me?${u.toString()}`);
  },
  deleteFile: (id: string) => request<void>(`/api/files/${id}`, { method: "DELETE" }),
  zipDownload: async (ids: string[]): Promise<Blob> => {
    const headers: HeadersInit = { "Content-Type": "application/json" };
    if (_token) (headers as Record<string, string>)["Authorization"] = `Bearer ${_token}`;
    const r = await fetch("/api/files/zip", {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({ ids }),
    });
    if (!r.ok) throw new ApiError(r.status, r.statusText);
    return r.blob();
  },
};

export const queryKeys = {
  authCheck: ["auth", "check"] as const,
  files: (order: "asc" | "desc") => ["files", order] as const,
  myFiles: (order: "asc" | "desc") => ["files", "me", order] as const,
  health: ["health"] as const,
};
